import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormSectionWithRows } from '@strata/react-form-section';
import { Hyperlink } from '@strata/react-hyperlink';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { useParams } from 'react-router';

import {
    type ReferralDetails as ReferralDetailsInterface,
    useGetAdditionalReferralDetailsQuery,
} from 'store/api/clientApi';
import { type ClaimDetails } from 'store/api/medbillingApi';
import { useGetPlaceOfServiceMapQuery } from 'store/api/referenceApi';

interface ReferralDetailsProps {
    claimDetails?: ClaimDetails;
    claimDetailsLoading?: boolean;
    referralDetails?: ReferralDetailsInterface;
    referralDetailsLoading?: boolean;
}

export const ReferralDetails = ({
    claimDetails = undefined,
    claimDetailsLoading = true,
    referralDetails = undefined,
    referralDetailsLoading = true,
    // eslint-disable-next-line complexity
}: ReferralDetailsProps) => {
    const { payerId } = useParams() as { payerId: string };
    const { data: additionalReferralDetails, isFetching: additionalReferralDetailsLoading } =
        useGetAdditionalReferralDetailsQuery(
            claimDetails?.referralId ? { payerId, referralId: claimDetails.referralId } : skipToken,
        );
    const { data: placeOfServiceMap, isLoading: placeOfServiceMapLoading } =
        useGetPlaceOfServiceMapQuery();

    return (
        <FormSectionWithRows
            sectionTitle="Referral Details"
            isLoading={
                claimDetailsLoading ||
                referralDetailsLoading ||
                additionalReferralDetailsLoading ||
                placeOfServiceMapLoading
            }
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Servicing Rate"
                        value={
                            <ServicingRate
                                servicingRate={referralDetails?.winningBidRate}
                                referentPriceType={referralDetails?.referentPriceType}
                            />
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Default Rate"
                        value={
                            <ServicingRate
                                servicingRate={additionalReferralDetails?.defaultRate}
                                referentPriceType={additionalReferralDetails?.referentPriceType}
                            />
                        }
                    />
                    <div style={{ width: '25%' }}>
                        <DetailLabel label="Referral Status" />
                        <ReferralStatusIndicator status={referralDetails?.referralStatus} />
                    </div>
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Place Of Service (From Referral)"
                        value={
                            referralDetails?.placeOfService &&
                            placeOfServiceMap?.[referralDetails.placeOfService]
                        }
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Referral ID"
                        value={
                            referralDetails?.friendlyId && (
                                // Link to referral details in Agora
                                <Hyperlink
                                    linkText={referralDetails.friendlyId}
                                    navUrl={`https://${
                                        process.env.REACT_APP_ENV !== 'prd'
                                            ? `${process.env.REACT_APP_ENV}-`
                                            : ''
                                    }agora.freemarkethealth.com/referrals/${
                                        referralDetails?.referralId
                                    }`}
                                />
                            )
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Route Rule"
                        value={referralDetails?.routeName}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Servicing Start Date"
                        value={formatDate(referralDetails?.auctionEnd, DateFormats.DATE_TIME)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Referral Expiration"
                        value={formatDate(referralDetails?.referralExpiration)}
                    />
                </>
            }
        />
    );
};
